import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TableTechnicalEventsComponent } from '../table-technical-events/table-technical-events.component';

@Component({
  selector: 'app-technical-events',
  templateUrl: './technical-events.component.html',
  styleUrls: ['./technical-events.component.scss'],
})
export class TechnicalEventsComponent {
  startRaw = '';
  endRaw = '';

  filters = new FormGroup({
    idTransaction: new FormControl<string>(''),
    type: new FormControl<string>(''),
    initialDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    endDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
  });

  public typeOptionsSelect: any[] = [
  { label: 'Todos', value: '' },
  { label: 'Token API', value: 'TOKEN API' },
  { label: 'Deposito', value: 'DEPOSITO' },
  { label: 'Saque', value: 'SAQUE' },
  { label: 'Cripto', value: 'CRIPTO' },
  { label: 'Reembolso', value: 'REEMBOLSO' },
  { label: 'TEF API', value: 'TEF API' },
];

  public isFilterCollapsed = false;
  public isLoaded = false;
  public logs: any[] = [];

  @ViewChild(TableTechnicalEventsComponent) table!: TableTechnicalEventsComponent;

  constructor(private toastr: ToastrService) { }

  ngOnInit(): void {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    this.filters.controls.initialDate.setValue(today);
    this.filters.controls.endDate.setValue(today);

    this.startRaw = this.formatBr(today);
    this.endRaw = this.formatBr(today);
  }

  onLogsLoaded(list: any[]): void {
    this.logs = list ?? [];
    this.isLoaded = true;
  }

  private asDate(v: any): Date {
    return v?.toDate ? v.toDate() : (v as Date);
  }

  private pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  private formatBr(d: Date): string {
    return `${this.pad(d.getDate())}/${this.pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  private ymd(d: Date): string {
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
  }

  private toIsoStart(d: Date): string {
    return `${this.ymd(d)}T00:00:00`;
  }

  private toIsoEnd(d: Date): string {
    return `${this.ymd(d)}T23:59:59`;
  }

  private parseDate(s: string): Date | null {
    if (!s) return null;
    const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;

    const day = +m[1];
    const month = +m[2] - 1;
    const year = +m[3];

    const d = new Date(year, month, day, 0, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }

  parseDateInput(ctrlName: 'initialDate' | 'endDate', raw: string): Date | null {
    const ctrl = this.filters.controls[ctrlName];

    const parsed = this.parseDate(raw);
    if (parsed) {
      ctrl.setValue(parsed, { emitEvent: false });
      ctrl.setErrors(null);
      return parsed;
    }

    if (raw?.trim()) {
      ctrl.setErrors({ matDatepickerParse: { text: raw } });
    } else {
      ctrl.setValue(null, { emitEvent: false });
      ctrl.setErrors({ required: true });
    }

    return null;
  }

  onDateChange(ctrlName: 'initialDate' | 'endDate', raw: string): void {
    const parsed = this.parseDateInput(ctrlName, raw);
    if (!parsed) return;

    const normalized = this.formatBr(parsed);
    if (ctrlName === 'initialDate') this.startRaw = normalized;
    else this.endRaw = normalized;
  }

  buscar(startRaw?: string, endRaw?: string): void {
    if (startRaw !== undefined) this.parseDateInput('initialDate', startRaw);
    if (endRaw !== undefined) this.parseDateInput('endDate', endRaw);

    this.filters.markAllAsTouched();
    this.filters.updateValueAndValidity({ emitEvent: false });

    const start = this.filters.controls.initialDate.value as Date | null;
    const end = this.filters.controls.endDate.value as Date | null;

    if (!start || !end) return;

    if (start > end) {
      this.toastr.error('Data inicial deve ser menor ou igual a data final!', 'Erro', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    if (this.filters.invalid) return;

    this.isLoaded = false;

    const startIso = this.toIsoStart(this.asDate(start));
    const endIso = this.toIsoEnd(this.asDate(end));

    const idTransaction = (this.filters.controls.idTransaction.value || '').trim();
    const type = this.filters.controls.type.value || '';

    this.table.page = 1;
    this.table.startDateIso = startIso;
    this.table.endDateIso = endIso;
    this.table.idTransaction = idTransaction;
    this.table.type = type;

    this.table.load();
  }

  toggleFilters(): void {
    this.isFilterCollapsed = !this.isFilterCollapsed;
  }

  async pasteIdTransaction(): Promise<void> {
    try {
      const text = (await navigator.clipboard.readText()).trim();
      this.filters.controls.idTransaction.setValue(text);
    } catch {
      this.toastr.error('Erro ao colar conteúdo.', 'Erro', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
    }
  }
}